import { isEmpty, omit, size } from 'lodash';
import { useCallback, useState } from 'react';

/**
 * This hook is used to get the error fields in the survey.
 * @param {*} allQuestions All questions in the survey.
 * @returns {object} The error fields.
 */
export const useErrorFields = (allQuestions) => {
  const [notAnswered, setNotAnswered] = useState([]);

  const getErrorFields = useCallback(() => {
    const errorFields = [];

    allQuestions.forEach((question) => {
      if (!question.isVisible) {
        return false;
      }

      const { questionType, questionOptions, answer } = question || {};

      if (questionType === 'task' || questionType === 'image_select' || questionType === 'five_second') {
        return false;
      }

      if (questionType === 'matrix') {
        const row = questionOptions.filter((option) => option.matrixOption === 'row');
        let isAnswerEmptyOrIncomplete = true;
        if (question.matrixType === 'single_select') {
          isAnswerEmptyOrIncomplete = isEmpty(answer) || row.length > Object.keys(answer).length;
        } else {
          isAnswerEmptyOrIncomplete = isEmpty(answer)
            || row.length > Object.keys(answer).length
            || Object.values(answer).some((arr) => arr.length === 0);
        }

        if (isAnswerEmptyOrIncomplete) {
          errorFields.push(question);
        }

        return false;
      }

      const isAnswerEmpty = !answer || isEmpty(answer);

      if (questionType === 'single_select' || questionType === 'multiple_select') {
        const otherOption = questionOptions.find((option) => option.optionType === 'other');
        const isOptionDescriptionEmpty = otherOption && isEmpty(answer?.optionDescription);
        if (isOptionDescriptionEmpty && !isAnswerEmpty && otherOption.id in answer) {
          errorFields.push(question);
        }

        if (questionType === 'multiple_select' && !isEmpty(question.multipleSelectionArray)) {
          const answerWithoutOptionDescription = omit(answer, 'optionDescription');
          if (size(question.multipleSelectionArray) === 1) {
            if (answerWithoutOptionDescription && size(answerWithoutOptionDescription) !== question.multipleSelectionArray[0]) {
              errorFields.push(question);
            }
          }
          if (size(question.multipleSelectionArray) === 2) {
            if ((answerWithoutOptionDescription && size(answerWithoutOptionDescription) < question.multipleSelectionArray[0])
              || (answerWithoutOptionDescription && size(answerWithoutOptionDescription) > question.multipleSelectionArray[1])
            ) {
              errorFields.push(question);
            }
          }
        }
      }

      if (isAnswerEmpty) {
        errorFields.push(question);
      }

      return false;
    });

    setNotAnswered(errorFields);
    return errorFields;
  }, [allQuestions, setNotAnswered]);

  const findErrorField = (question) => notAnswered.find((answer) => question.id === answer.id);

  const removeErrorField = (questionId) => setNotAnswered((notAnswereds) => notAnswereds.filter((answer) => answer.id !== questionId));

  return {
    getErrorFields,
    findErrorField,
    removeErrorField,
  };
};
